import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { setFocus } from '@noriginmedia/norigin-spatial-navigation'
import { useNetworkMonitor } from './NetworkMonitorProvider'
import ErrorModalTS from '../../components/ErrorModal/ErrorModalTS'
import networkStatusNotifier from './networkStatusNotifier'
import { verifyConnectivity } from './verifyConnectivity'

import NavigationModeProvider from '../../../../shared/providers/NavigationModeProvider'
import { ErrorButton, NetworkMonitorWrapperProps } from './types'
import { exitApp, adjustScale } from '../../../../shared/helpers.js'
import { useSidePanel } from '../../components/SidePanel/SidePanelProvider'
import {
  KeyTypeEnum,
  useKeyDown,
} from '../../../../shared/providers/KeyDownProvider'

const PORTAL_ROOT_ID = 'network-error-modal-root'

const ERROR_MODAL_TITLE = 'Connection Error'
const ERROR_MODAL_MESSAGE =
  'Unable to access the internet. Please connect and relaunch the app.'

const createErrorModalButtons = (): ErrorButton[] => [
  {
    label: 'Exit',
    name: 'exitButton',
    focusKey: 'exitButton',
    onEnterPress: exitApp,
    onClick: exitApp,
  },
]

function NetworkMonitorWrapper({
  children,
  Spinner,
}: NetworkMonitorWrapperProps) {
  const { isDisconnected, lastFocusKey, suppressAutoErrorModal } =
    useNetworkMonitor()
  const { setKeepSidePanelOpened } = useSidePanel()
  const [hasXhrNetworkError, setHasXhrNetworkError] = useState(false)
  const [isModalDismissed, setIsModalDismissed] = useState(false)
  const isErrorModalVisible =
    hasXhrNetworkError || (isDisconnected && !suppressAutoErrorModal)
  const handleBackKey = useCallback(() => {
    if (isErrorModalVisible) {
      setHasXhrNetworkError(false)
      setIsModalDismissed(true)
    }
  }, [isErrorModalVisible])

  const backKeyOptions = useMemo(
    () => ({ stopPropagation: isErrorModalVisible }),
    [isErrorModalVisible]
  )

  useKeyDown(KeyTypeEnum.Back, handleBackKey, backKeyOptions)

  const errorModalButtons = createErrorModalButtons()

  const portalRootRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (isDisconnected) {
      setIsModalDismissed(false)
      setKeepSidePanelOpened(true)
    } else {
      setTimeout(() => setKeepSidePanelOpened(false), 0)
    }
  }, [isDisconnected, setKeepSidePanelOpened])

  useEffect(() => {
    // Create or get portal target element
    let portalRoot = document.getElementById(PORTAL_ROOT_ID)

    if (!portalRoot) {
      portalRoot = document.createElement('div')
      portalRoot.id = PORTAL_ROOT_ID
      document.body.appendChild(portalRoot)
    }

    portalRootRef.current = portalRoot

    // Cleanup: only remove if we created it
    return () => {
      if (portalRoot && portalRoot.parentNode && !portalRoot.hasChildNodes()) {
        portalRoot.parentNode.removeChild(portalRoot)
      }
    }
  }, [])

  // Subscribe to XHR network errors — verify actual connectivity before showing modal
  useEffect(() => {
    let isVerifying = false

    const unsubscribe = networkStatusNotifier.subscribe(async () => {
      if (isVerifying) return
      isVerifying = true

      const isConnected = await verifyConnectivity()

      if (!isConnected) {
        setHasXhrNetworkError(true)
      }
      isVerifying = false
    })

    return unsubscribe
  }, [])

  // Restore focus when connection is recovered
  useEffect(() => {
    if (!isDisconnected && lastFocusKey) {
      setFocus(lastFocusKey)
    }
  }, [isDisconnected, lastFocusKey])

  const adjustErrorModalScale = () =>
    setTimeout(() => {
      const portalRoot = document.getElementById(PORTAL_ROOT_ID)
      const errorModalElement = portalRoot?.querySelector('.error-modal')
      adjustScale(errorModalElement as HTMLElement)
    }, 0)

  // XHR network error: render ONLY the error modal, no children, exit the App is the only option
  // some devices doesn't trigger 'offline/online' events, so we need to rely on XHR request errors
  // to detect network issues. In this scenario, we won't be able to recover the connection status
  if (hasXhrNetworkError) {
    adjustErrorModalScale()
    return (
      <NavigationModeProvider>
        <ErrorModalTS
          title={ERROR_MODAL_TITLE}
          description={ERROR_MODAL_MESSAGE}
          buttons={errorModalButtons}
          hideStationLogo
          errorType="internet"
        />
      </NavigationModeProvider>
    )
  }

  // Portal overlay (children stay behind modal)
  let modalContent = null
  if (
    isDisconnected &&
    !suppressAutoErrorModal &&
    !isModalDismissed &&
    portalRootRef.current
  ) {
    adjustErrorModalScale()
    modalContent = createPortal(
      <NavigationModeProvider>
        <ErrorModalTS
          title={ERROR_MODAL_TITLE}
          description={ERROR_MODAL_MESSAGE}
          buttons={errorModalButtons}
          componentType="errorModal"
          hideStationLogo
          errorType="internet"
        />
      </NavigationModeProvider>,
      portalRootRef.current as Element
    )
  }

  return (
    <NavigationModeProvider>
      <Spinner visible={false} fullscreen />
      {children}
      {modalContent}
    </NavigationModeProvider>
  )
}

export default NetworkMonitorWrapper
